/**
 * One duel (ADR 0076): the two players and their clocks around the board, where the game
 * stands, and what this player may do about it.
 *
 * HTTP writes and the socket says when to refetch (ADR 0029); with the socket closed the
 * duel polls, every 2 s on the clock and every 15 s by correspondence. The clocks shown are
 * cosmetic: the server keeps time, and resyncs them with every refetch. A realtime duel
 * hands the board to `RealtimeArea`, which relays frames and sends the log (ADR 0077).
 */
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { getDuel, type DuelFrame, type DuelModule, type Side } from "@/duels/registry";
import { useTenant } from "@/core/tenant/TenantProvider";
import { useRealtimeOpen, useTopic } from "@/core/realtime/RealtimeProvider";
import { Button } from "@/core/ui";
import {
  duelTopic,
  opponentOf,
  postDraw,
  postMove,
  useDuel,
  useDuelAction,
  type DuelDetail,
} from "./api";
import {
  formatClock,
  outcomeFor,
  useNow,
  useReason,
  useTimeControlLabel,
  useTimeLeft,
} from "./format";
import { RealtimeArea } from "./RealtimeArea";

export function DuelPage() {
  const { key = "", id = "" } = useParams();
  const { t } = useTranslation();
  const tenant = useTenant();
  const module = tenant.enabled_games.includes(key) ? getDuel(key) : undefined;
  if (tenant.enabled_games.length === 0)
    return <p className="bt-page-message">{t("games.disabled")}</p>;
  if (!module || !/^\d+$/.test(id)) return <p className="bt-page-message">{t("games.unknown")}</p>;
  return <Duel duelId={Number(id)} module={module} />;
}

function Duel({ duelId, module }: { duelId: number; module: DuelModule }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const socketOpen = useRealtimeOpen();
  const duel = useDuel(duelId, socketOpen);
  const frameListeners = useRef(new Set<(frame: DuelFrame) => void>());
  const queryKey = ["duels", "duel", duelId];

  useTopic(duelTopic(duelId), (frame) => {
    if (frame.type === "duel.changed") void queryClient.invalidateQueries({ queryKey });
    if (frame.type === "duel.frame") frameListeners.current.forEach((listen) => listen(frame.data));
  });

  const data = duel.data;

  if (duel.isPending) return <p className="bt-page-message">{t("app.loading")}</p>;
  if (duel.isError || !data)
    return <p className="bt-page-message bt-page-message--error">{t("duels.load_error")}</p>;
  return (
    <DuelView
      duel={data}
      fetchedAt={duel.dataUpdatedAt}
      module={module}
      frameListeners={frameListeners.current}
      socketOpen={socketOpen}
    />
  );
}

function DuelView({
  duel,
  fetchedAt,
  module,
  frameListeners,
  socketOpen,
}: {
  duel: DuelDetail;
  fetchedAt: number;
  module: DuelModule;
  frameListeners: Set<(frame: DuelFrame) => void>;
  socketOpen: boolean;
}) {
  const { t } = useTranslation();
  const ns = module.namespace;
  const queryClient = useQueryClient();
  const label = useTimeControlLabel();
  const reason = useReason();
  const action = useDuelAction();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const queryKey = ["duels", "duel", duel.id];

  const me: Side = duel.my_side ?? "white";
  const them: Side = me === "white" ? "black" : "white";
  const opponent = opponentOf(duel);
  const running = duel.status === "active" || duel.status === "ready";
  const clockRunning = duel.status === "active" && duel.clock !== null && duel.to_move !== null;
  const now = useNow(clockRunning ? 100 : 1000, running);
  const elapsed = now - fetchedAt;

  const remaining = (side: Side): number | null => {
    if (!duel.clock) return null;
    const left = duel.clock[side] - (clockRunning && duel.to_move === side ? elapsed : 0);
    return Math.max(0, left);
  };
  // A clock or a deadline reached zero here: the server decides what it means.
  const deadlineLeft = duel.deadline_at
    ? Date.parse(duel.deadline_at) - Date.parse(duel.server_now) - elapsed
    : null;
  const due =
    (clockRunning && (remaining(duel.to_move!) ?? 1) <= 0) ||
    (running && deadlineLeft !== null && deadlineLeft <= 0);
  useEffect(() => {
    if (!due) return;
    const timer = window.setTimeout(
      () => void queryClient.invalidateQueries({ queryKey: ["duels", "duel", duel.id] }),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [due, fetchedAt, duel.id, queryClient]);

  const play = useCallback(
    async (move: string) => {
      setBusy(true);
      setNotice("");
      try {
        queryClient.setQueryData(queryKey, await postMove(duel.id, move));
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [duel.id, queryClient],
  );

  const draw = (choice: "offer" | "accept" | "decline") =>
    postDraw(duel.id, choice)
      .then((next) => queryClient.setQueryData(queryKey, next))
      .catch(() => setNotice(t("duels.action_failed")));

  const act = (choice: "accept" | "decline" | "resign") =>
    action.mutate(
      { id: duel.id, action: choice },
      { onError: () => setNotice(t("duels.action_failed")) },
    );

  const seated = duel.my_side !== null;
  const outcome = outcomeFor(duel);
  const canMove = seated && !busy && duel.to_move === duel.my_side && running && !duel.realtime;

  return (
    <div className="bt-duel">
      <div className="bt-duel__main">
        <header className="bt-page-header bt-duel__header">
          <Link to={`/duels/${duel.game_key}`} className="bt-game-run__back">
            {t("name", { ns })}
          </Link>
          <h1 className="bt-page-title">
            {opponent ? t("duels.versus", { name: opponent.name }) : t("duels.no_opponent")}
          </h1>
          <p className="bt-page-subtitle">
            {label(duel.time_control)}
            {duel.is_rated ? `, ${t("duels.rated")}` : `, ${t("duels.unrated")}`}
          </p>
        </header>

        {seated ? (
          <PlayerStrip
            duel={duel}
            side={them}
            name={opponent?.name ?? t("duels.deleted_player")}
            ns={ns}
            clock={remaining(them)}
            deadline={duel.to_move === them && !duel.clock ? deadlineLeft : null}
          />
        ) : null}

        <Board
          duel={duel}
          module={module}
          canMove={canMove}
          play={play}
          frameListeners={frameListeners}
          socketOpen={socketOpen}
        />

        {seated ? (
          <PlayerStrip
            duel={duel}
            side={me}
            name={t("games.you")}
            ns={ns}
            clock={remaining(me)}
            deadline={duel.to_move === me && !duel.clock ? deadlineLeft : null}
          />
        ) : null}

        <p className="bt-duel__status" role="status">
          <StatusLine duel={duel} ns={ns} deadlineLeft={deadlineLeft} />
        </p>
        {notice ? (
          <p className="bt-duel__notice" role="alert">
            {notice}
          </p>
        ) : null}

        {duel.status === "finished" ? (
          <div className="bt-duel__result" data-outcome={outcome ?? undefined}>
            <h2 className="bt-duel__result-title">
              {outcome ? t(`duels.outcome.${outcome}`) : t("duels.outcome.draw")}
            </h2>
            <p>{reason(ns, duel.result_reason)}</p>
            {duel.my_side && duel.rating_change[duel.my_side] !== undefined ? (
              <p className="bt-duel__rating-change">
                {t("duels.rating_change", {
                  change: signed(duel.rating_change[duel.my_side]!),
                  rating: duel.ratings[duel.my_side],
                })}
              </p>
            ) : null}
            <Link
              to={`/duels/${duel.game_key}`}
              className="bt-button bt-button--secondary bt-button--md"
            >
              {t("duels.back_to_lobby")}
            </Link>
          </div>
        ) : null}

        <div className="bt-duel__actions">
          {duel.status === "invited" && !duel.is_challenger ? (
            <>
              <Button onClick={() => act("accept")} loading={action.isPending}>
                {t("duels.accept")}
              </Button>
              {duel.invitee ? (
                <Button variant="ghost" onClick={() => act("decline")}>
                  {t("duels.decline")}
                </Button>
              ) : null}
            </>
          ) : null}
          {duel.status === "invited" && duel.is_challenger ? (
            <Button variant="secondary" onClick={() => act("decline")}>
              {t("duels.withdraw")}
            </Button>
          ) : null}
          {seated && duel.draw_offers && duel.status === "active" ? (
            duel.draw_offer === them ? (
              <>
                <Button variant="secondary" onClick={() => void draw("accept")}>
                  {t("duels.draw_accept")}
                </Button>
                <Button variant="ghost" onClick={() => void draw("decline")}>
                  {t("duels.draw_decline")}
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                disabled={duel.draw_offer === me}
                onClick={() => void draw("offer")}
              >
                {duel.draw_offer === me ? t("duels.draw_offered") : t("duels.draw_offer")}
              </Button>
            )
          ) : null}
          {seated && running ? (
            <Button
              variant="ghost"
              onClick={() => {
                if (duel.status === "ready" || window.confirm(t("duels.resign_confirm")))
                  act("resign");
              }}
            >
              {duel.status === "ready" ? t("duels.abort") : t("duels.resign")}
            </Button>
          ) : null}
        </div>
      </div>

      <aside className="bt-duel__aside">
        <h2 className="bt-game-run__aside-title">{t("duels.rules")}</h2>
        <p className="bt-duel-lobby__rules">{t("rules", { ns })}</p>
      </aside>
    </div>
  );
}

function signed(value: number): string {
  return value >= 0 ? `+${value}` : `−${Math.abs(value)}`;
}

function Board({
  duel,
  module,
  canMove,
  play,
  frameListeners,
  socketOpen,
}: {
  duel: DuelDetail;
  module: DuelModule;
  canMove: boolean;
  play: (move: string) => Promise<void>;
  frameListeners: Set<(frame: DuelFrame) => void>;
  socketOpen: boolean;
}) {
  const { t } = useTranslation();
  if (!duel.my_side) return null;
  if (duel.realtime) {
    return module.RealtimeBoard ? (
      <RealtimeArea
        duel={duel}
        Board={module.RealtimeBoard}
        frameListeners={frameListeners}
        socketOpen={socketOpen}
      />
    ) : null;
  }
  if (!module.Board || duel.view === null) return null;
  return (
    <Suspense fallback={<p className="bt-page-message">{t("app.loading")}</p>}>
      <module.Board
        view={duel.view}
        moves={duel.moves}
        side={duel.my_side}
        toMove={duel.status === "active" || duel.status === "ready" ? duel.to_move : null}
        canMove={canMove}
        play={play}
      />
    </Suspense>
  );
}

function PlayerStrip({
  duel,
  side,
  name,
  ns,
  clock,
  deadline,
}: {
  duel: DuelDetail;
  side: Side;
  name: string;
  ns: string;
  clock: number | null;
  deadline: number | null;
}) {
  const { t } = useTranslation();
  const timeLeft = useTimeLeft();
  const rating = duel.ratings[side];
  const here = duel.present.includes(side);
  const active = duel.to_move === side && (duel.status === "active" || duel.status === "ready");
  return (
    <div className="bt-duel-player" data-side={side} data-to-move={active || undefined}>
      <span className="bt-duel-player__side">{t("sides." + side, { ns })}</span>
      <span className="bt-duel-player__name">{name}</span>
      {rating !== undefined ? <span className="bt-duel-player__rating">{rating}</span> : null}
      {duel.status === "active" || duel.status === "ready" ? (
        <span className="bt-duel-player__here" data-here={here || undefined}>
          {here ? t("duels.here") : t("duels.away")}
        </span>
      ) : null}
      {clock !== null ? (
        <span
          className="bt-duel-player__clock"
          data-low={clock < 20_000 || undefined}
          aria-label={t("duels.clock_label", { time: formatClock(clock) })}
        >
          {formatClock(clock)}
        </span>
      ) : deadline !== null && deadline > 0 ? (
        <span className="bt-duel-player__deadline">
          {t("duels.deadline", { left: timeLeft(deadline) })}
        </span>
      ) : null}
    </div>
  );
}

function StatusLine({
  duel,
  ns,
  deadlineLeft,
}: {
  duel: DuelDetail;
  ns: string;
  deadlineLeft: number | null;
}) {
  const { t } = useTranslation();
  const opponent = opponentOf(duel);
  const name = opponent?.name ?? "";
  switch (duel.status) {
    case "invited":
      if (!duel.is_challenger)
        return <>{t("duels.state.challenges_you", { name: duel.challenger?.name ?? "" })}</>;
      return (
        <>
          {duel.invitee
            ? t("duels.state.waiting_accept", { name: duel.invitee.name })
            : t("duels.state.open_mine")}
        </>
      );
    case "ready":
      if (duel.realtime || duel.to_move === null)
        return <>{t("duels.state.waiting_presence", { name })}</>;
      return (
        <>
          {t(
            duel.to_move === duel.my_side
              ? "duels.state.first_move_you"
              : "duels.state.first_move_them",
            {
              name,
              side: t(`sides.${duel.to_move}`, { ns }),
              seconds: Math.max(0, Math.ceil((deadlineLeft ?? 0) / 1000)),
            },
          )}
        </>
      );
    case "active":
      if (duel.realtime) return <>{t("duels.state.playing")}</>;
      return (
        <>
          {duel.your_turn
            ? duel.draw_offer && duel.draw_offer !== duel.my_side
              ? t("duels.state.your_move_draw", { name })
              : t("duels.state.your_move")
            : t("duels.state.their_move", { name })}
        </>
      );
    case "finished":
      return null;
    default:
      return <>{t(`duels.status.${duel.status}`)}</>;
  }
}
